
import { IUser, useApiErrorHandler } from "@sagecentral/core-ui"
import   useErrorHandlerContext, { OnErrorSignature }   from "./useErrorHandlerContext";
import { useAuditScope } from "components/shared/audit";
import useCurrentUser from "./useCurrentUser";
import useRequestVerificationToken from "utils/useRequestVerificationToken";

type FetcherSignature = (url: RequestInfo | URL, init?: RequestInit, onError?: OnErrorSignature, token?: string) => Promise<Response>;
const emptyFetcher: FetcherSignature = (async (url: RequestInfo | URL, init?: RequestInit) => {
    console.warn('empty fetcher called');
    return new Response();
});

const useCentralApi = (user?: IUser | null, json?: boolean) => {    
    const apibaseurl = process.env.REACT_APP_API_URL;
    const auditUuid = useAuditScope();
    const errorHandler = useApiErrorHandler();
    const errorHandlerContext  = useErrorHandlerContext();
    const currentUser = useCurrentUser();

    const accessToken = user?.access_token ?? currentUser?.access_token;
    const getToken = useRequestVerificationToken(user?.id_token ?? currentUser?.id_token ?? '');                    
    
    const useJson = json === undefined || json;
    const fetcher: FetcherSignature = async (url: RequestInfo | URL, init?: RequestInit, onError?: OnErrorSignature, token?: string) => {
        
        if (accessToken || token) {
            const manageRequest = async () => {
                let baseHeaders: HeadersInit = {                    
                    'Authorization': `Bearer ${accessToken || token}`
                };
                if (useJson) {
                    baseHeaders['Content-Type'] = 'application/json';
                }
                if (auditUuid) {
                    baseHeaders['X-Audit-UUID'] = auditUuid;
                }
                 // Identify the api request whether it is comeing from react or classic in mvc custome authorization filter
                baseHeaders['X-SC-App'] = 'SC-React-App';
                
                // Only include verification token for non-GET requests
                const method = init?.method?.toUpperCase() || 'GET';
                if (method !== 'GET') {                    
                    baseHeaders['__RequestVerificationToken'] = await getToken();
                }

                const resp = await fetch(`${apibaseurl}${url}`, {
                    ...init,
                    headers: {
                        ...baseHeaders,
                        ...init?.headers
                    },
                    mode: 'cors',
                    credentials: 'include'
                });
                return resp;
            }

            try {
                const resp = await manageRequest();
                if (!resp.ok) {
                    let handled = false;

                    if (onError) {
                        handled = await onError(resp);
                    } 
                    
                    if (!handled && errorHandlerContext) {
                        handled = await errorHandlerContext(resp);
                    }
    
                    if (!handled) {
                        if (resp.status === 401) {
                            var problemDetails = await resp.json();
                            if (problemDetails !== undefined && problemDetails !== null && problemDetails.type === "AADB2C90118") {
                                window.location.replace(process.env.REACT_APP_CLASSIC_URL + '/Account/ForcePasswordReset' || '');
                                return resp;

                            } else {
                                throw new Error('Error fetching data', { cause: resp.status });
                            }
                       }
                  
                        var referenceId = await resp.text();
                        errorHandler(new Error("Reference Id: " + referenceId, { cause: resp.status}));
                    }
                }
            
                if(resp.url.endsWith("/Account/Deactivated")){
                    throw new Error('Account Deactivated', { cause: 401 });
                }

                return resp;
            } catch (error) {
                errorHandler(error);
                throw error;
            }
        } else {
            return emptyFetcher(url, init);
        }
    };

    return fetcher;
}

export default useCentralApi;